import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { Card, Table, Badge, Button, Modal, Input, FormStack } from "@marekl/adminer/components";
import { notifySuccess, notifyError } from "@marekl/adminer/api";
import {
  useMerchKinds,
  useCreateMerchKind,
  useUpdateMerchKind,
} from "../../../lib/queries/merchKinds";
import type { MerchKind } from "../../../lib/queries/merchKinds";
import styles from "./MerchKindsCard.module.css";

// ─── Modal ────────────────────────────────────────────────────────────────────

interface MerchKindModalProps {
  open: boolean;
  /** null = create mode */
  kind: MerchKind | null;
  onClose: () => void;
}

interface FormState {
  code: string;
  name: string;
  active: boolean;
}

const EMPTY_FORM: FormState = { code: "", name: "", active: true };

function kindToForm(mk: MerchKind): FormState {
  return { code: mk.code, name: mk.name, active: mk.active };
}

function MerchKindModal({ open, kind, onClose }: MerchKindModalProps) {
  const isEdit = kind !== null;
  const createKind = useCreateMerchKind();
  const updateKind = useUpdateMerchKind(kind?.id ?? 0);
  const isPending = createKind.isPending || updateKind.isPending;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    if (open) {
      setForm(kind ? kindToForm(kind) : EMPTY_FORM);
      setErrors({});
    }
  }, [open, kind]);

  function validate(): boolean {
    const e: typeof errors = {};
    if (!isEdit && !form.code.trim()) e.code = "Povinné pole";
    if (!form.name.trim()) e.name = "Povinné pole";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    try {
      if (isEdit) {
        await updateKind.mutateAsync({ name: form.name.trim(), active: form.active });
        notifySuccess("Produkt byl upraven.");
      } else {
        await createKind.mutateAsync({
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          active: form.active,
        });
        notifySuccess("Produkt byl vytvořen.");
      }
      onClose();
    } catch (err) {
      notifyError(err);
    }
  }

  return (
    <Modal
      open={open}
      title={isEdit ? `Upravit: ${kind!.name}` : "Nový nevoucherový produkt"}
      onClose={onClose}
      width={440}
      footer={
        <>
          <Button onClick={onClose}>Zrušit</Button>
          <Button
            variant="primary"
            loading={isPending}
            onClick={handleSubmit as unknown as React.MouseEventHandler}
          >
            {isEdit ? "Uložit" : "Vytvořit"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <FormStack>
          <Input
            id="mk-code"
            label="Kód *"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            error={errors.code}
            disabled={isEdit}
            autoComplete="off"
            spellCheck={false}
            className={styles.mono}
          />
          <Input
            id="mk-name"
            label="Název *"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={errors.name}
            autoComplete="off"
          />
          <div className={styles.activeField}>
            <label className={styles.checkLabel}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Aktivní
            </label>
          </div>
        </FormStack>
      </form>
    </Modal>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function MerchKindsCard() {
  const { data: kinds = [], isLoading } = useMerchKinds();
  const [modal, setModal] = useState<
    { kind: "closed" } | { kind: "create" } | { kind: "edit"; mk: MerchKind }
  >({ kind: "closed" });

  return (
    <Card
      header={
        <Card.Header
          title="Nevoucherové produkty"
          subtitle="Typy položek bez papírového voucheru — e-shop zboží, vstupenky, balíčky."
          actions={
            <Button
              variant="primary"
              size="sm"
              onClick={() => setModal({ kind: "create" })}
            >
              <Plus size={14} aria-hidden="true" />
              Nový
            </Button>
          }
        />
      }
      bodyPadding="none"
    >
      <Table<MerchKind>
        rowKey={(mk) => mk.id}
        columns={[
          {
            key: "code",
            header: "Kód",
            tdClass: styles.mono,
            render: (mk) => mk.code,
          },
          {
            key: "name",
            header: "Název",
            render: (mk) => (
              <span className={styles.nameCell}>
                {mk.name}
                {!mk.active && (
                  <Badge variant="muted" className={styles.inactiveBadge}>
                    Neaktivní
                  </Badge>
                )}
              </span>
            ),
          },
          {
            key: "actions",
            header: "",
            render: (mk) => (
              <Table.Actions>
                <Table.Action onClick={() => setModal({ kind: "edit", mk })}>
                  Upravit
                </Table.Action>
              </Table.Actions>
            ),
          },
        ]}
        rows={kinds}
        empty={isLoading ? "Načítám…" : "Žádné nevoucherové produkty."}
      />

      <MerchKindModal
        open={modal.kind !== "closed"}
        kind={modal.kind === "edit" ? modal.mk : null}
        onClose={() => setModal({ kind: "closed" })}
      />
    </Card>
  );
}
